import { c, card, tbl } from '../../theme';
import { fmtCount, fmtClock, fmtPct, STATUS, statusColor } from './format';

// One row per chosen pod, one block of columns per period — the old rollup's
// columns (exec/PodRollup.jsx). Its "Handled" is inbound only: the pod's
// inbound conversations, callbacks included, each counted once per pod. Agent
// Out is the rest, the calls agents dialled themselves.
//
// A range (Yesterday, YTD, Last Month, Custom) shows the old custom block, Off
// and Aban included; Today and MTD show the old default set. Occ % is ours: the
// old rollup offered it only in its column picker.

const COL = {
  offered: { key: 'offered', label: 'Off', title: 'Inbound conversations offered to the pod\'s queues', fmt: fmtCount },
  handled: { key: 'inbound', label: 'Handled', title: 'Inbound conversations that reached an agent, callbacks included — each once per pod', fmt: fmtCount },
  agentOut: { key: 'agentOut', label: 'Agent Out', title: 'Calls agents dialled themselves — not part of Handled', fmt: fmtCount },
  abandons: { key: 'abandons', label: 'Aban', title: 'Voice + VIP abandons', fmt: fmtCount },
  serviceLevel: { key: 'serviceLevel', label: 'SL%', title: 'Service level, Voice + VIP', fmt: fmtPct, status: STATUS.serviceLevel },
  aht: { key: 'aht', label: 'AHT', title: 'Average handle time, Voice + VIP', fmt: fmtClock, status: STATUS.aht },
  // The old rollup's ASA is the ASA gauge's, per pod: the chosen channels
  // for a range, Voice + VIP + Callback for Today and MTD.
  asa: { key: 'asa', label: 'ASA', title: 'Speed of answer over the channels chosen, as the ASA gauge', fmt: fmtClock },
  asaPhone: { key: 'asaPhone', label: 'ASA', title: 'Speed of answer, Voice + VIP + Callback', fmt: fmtClock },
  // Uncoloured, as the old rollup's Occ% was: Athens has not set bands for it here.
  occupancy: { key: 'occupancy', label: 'Occ %', title: 'Time handling interactions over time On Queue, the pod\'s agents together, all channels', fmt: fmtPct },
};
const RANGE_COLS = [COL.offered, COL.handled, COL.agentOut, COL.abandons, COL.serviceLevel, COL.aht, COL.asa, COL.occupancy];
const PERIOD_COLS = [COL.handled, COL.agentOut, COL.serviceLevel, COL.aht, COL.asaPhone, COL.occupancy];

export default function TeamRollup({ periods, range = false }) {
  const COLS = range ? RANGE_COLS : PERIOD_COLS;
  // Row order: the leading period's, by Handled, as the old rollup.
  const pods = periods[0].rollup.map((r) => r.pod);
  const rowOf = (p, pod) => p.rollup.find((r) => r.pod === pod);
  return (
    <section style={s.card} aria-label="Pod rollup">
      <h2 style={s.h2}>Pods</h2>
      <div style={{ overflowX: 'auto' }}>
        <table style={s.table}>
          <thead>
            {periods.length > 1 && (
              <tr>
                <th style={s.group} />
                {periods.map((p) => <th key={p.key} colSpan={COLS.length} style={s.group}>{p.label}</th>)}
              </tr>
            )}
            <tr>
              <th style={s.th}>Pod</th>
              {periods.map((p) => COLS.map((col) => <th key={`${p.key}.${col.key}`} style={s.thNum} title={col.title}>{col.label}</th>))}
            </tr>
          </thead>
          <tbody>
            {pods.map((pod) => (
              <tr key={pod}>
                <td style={{ ...s.td, fontWeight: 550 }}>{pod}</td>
                {periods.map((p) => {
                  const r = rowOf(p, pod) || {};
                  return COLS.map((col) => {
                    const v = r[col.key] ?? null;
                    const tone = col.status ? col.status(v) : null;
                    return (
                      <td key={`${p.key}.${col.key}`} style={{ ...s.num, color: statusColor(tone), fontWeight: tone ? 600 : 400 }}>
                        {col.fmt(v)}{col.key === 'inbound' && r.complete === false ? '*' : ''}
                      </td>
                    );
                  });
                })}
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      <p style={s.foot}>
        Off and Handled count each conversation once per pod. Service level, AHT, speed of answer and
        abandons are Voice and VIP. Occupancy is time handling interactions over time On Queue, across
        every channel. * Some days in this period have no count stored yet.
      </p>
    </section>
  );
}

const s = {
  card:   { ...card.base, padding: '14px 0 10px' },
  h2:     { fontSize: '13px', fontWeight: 650, color: c.text, margin: '0 18px 8px' },
  table:  { width: '100%', borderCollapse: 'collapse', fontSize: '12.5px' },
  group:  { ...tbl.thSm, textAlign: 'center', borderBottom: `1px solid ${c.border}`, color: c.textStrong },
  th:     { ...tbl.th, textAlign: 'left' },
  thNum:  { ...tbl.thR, whiteSpace: 'nowrap' },
  td:     { ...tbl.td, color: c.textStrong, whiteSpace: 'nowrap' },
  num:    { ...tbl.tdR, color: c.textStrong },
  foot:   { fontSize: '11px', color: c.textMuted, margin: '8px 18px 0', lineHeight: 1.45 },
};
